import { Box, Flex, Text, VStack, useT } from '@minetrace/ui'
import { motion } from 'framer-motion'
import { LuArrowUpRight, LuPackage, LuUser } from 'react-icons/lu'
import { useNavigate } from 'react-router-dom'
import type {
  IndiceExpedienteOrigenResponse,
  IndiceRelacionadoResponse,
} from '../../../api/models/response/indiceDigital'
import { brand, themeColors } from '../../../theme'
import { formatDate } from '../../../lib/dates'

const MotionBox = motion.create(Box)

/**
 * Expedientes de lote apuntados desde éste. NO FOLIAN AQUÍ: cada uno es un expediente
 * autónomo con su propio índice, su propio foliado y su propio CVD, así que se enlaza
 * en vez de inlinearse — inlinearlos rompería la numeración corrida de este índice.
 */
export function RelacionadosTab({
  relacionados,
  origen,
}: {
  relacionados: IndiceRelacionadoResponse[]
  /** Expediente del minero del que nace este lote (sólo en expedientes de lote). */
  origen: IndiceExpedienteOrigenResponse | null
}) {
  const { t } = useT()
  const navigate = useNavigate()

  // Un expediente de lote no apunta a otros lotes: lo relevante es de dónde nace.
  if (origen) {
    const open = () => navigate(`/app/identidad/expedientes/${encodeURIComponent(origen.codigo)}/electronico`)
    return (
      <VStack alignItems="stretch" gap={4}>
        <Box>
          <Text fontSize="sm" fontWeight="700" color={themeColors.text}>
            {t('expedientes.electronico.relacionados.origenTitle')}
          </Text>
          <Text fontSize="xs" color={themeColors.textMuted} mt={1}>
            {t('expedientes.electronico.relacionados.origenSubtitle')}
          </Text>
        </Box>
        <MotionBox
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          bg={themeColors.bgPanel}
          border="1px solid"
          borderColor={themeColors.border}
          borderRadius="xl"
          p={4}
          cursor="pointer"
          _hover={{ borderColor: brand.green, bg: `${brand.green}06` }}
          onClick={open}
        >
          <Flex alignItems="center" gap={3}>
            <Flex
              w="36px"
              h="36px"
              borderRadius="lg"
              bg={`${brand.green}12`}
              color={brand.green}
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <LuUser size={17} />
            </Flex>
            <Box flex="1" minW={0}>
              <Flex alignItems="center" gap={2}>
                <Text fontSize="sm" fontWeight="600" color={themeColors.text} truncate>
                  {origen.titular ?? origen.codigo}
                </Text>
                <Text
                  fontSize="10px"
                  fontWeight="700"
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                  color={brand.green}
                  bg={`${brand.green}12`}
                  px={2}
                  py="1px"
                  borderRadius="full"
                  flexShrink={0}
                >
                  {t('expedientes.electronico.relacionados.origenTag')}
                </Text>
              </Flex>
              <Flex gap={3} flexWrap="wrap">
                <Text fontSize="11px" fontFamily="monospace" color={themeColors.textMuted}>{origen.codigo}</Text>
                {origen.ruc && (
                  <Text fontSize="11px" color={themeColors.textMuted}>RUC {origen.ruc}</Text>
                )}
              </Flex>
            </Box>
            <Flex alignItems="center" gap={1} color={brand.green} flexShrink={0}>
              <Text fontSize="xs" fontWeight="700">
                {t('expedientes.electronico.relacionados.open')}
              </Text>
              <LuArrowUpRight size={14} />
            </Flex>
          </Flex>
        </MotionBox>
      </VStack>
    )
  }

  return (
    <VStack alignItems="stretch" gap={4}>
      <Box>
        <Text fontSize="sm" fontWeight="700" color={themeColors.text}>
          {t('expedientes.electronico.relacionados.title')}
        </Text>
        <Text fontSize="xs" color={themeColors.textMuted} mt={1}>
          {t('expedientes.electronico.relacionados.subtitle')}
        </Text>
      </Box>

      {relacionados.length === 0 && (
        <Text fontSize="sm" color={themeColors.textMuted} py={8} textAlign="center">
          {t('expedientes.electronico.relacionados.empty')}
        </Text>
      )}

      {relacionados.map((r, idx) => {
        const target = r.codigoDestino
        return (
          <MotionBox
            key={r.itemId}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: Math.min(idx, 10) * 0.025 }}
            bg={themeColors.bgPanel}
            border="1px solid"
            borderColor={themeColors.border}
            borderRadius="xl"
            p={4}
            cursor={target ? 'pointer' : 'default'}
            _hover={target ? { borderColor: brand.green, bg: `${brand.green}06` } : {}}
            onClick={target
              ? () => navigate(`/app/identidad/expedientes/${encodeURIComponent(target)}/electronico`)
              : undefined}
          >
            <Flex alignItems="center" gap={3}>
              <Flex
                w="36px"
                h="36px"
                borderRadius="lg"
                bg={`${brand.green}12`}
                color={brand.green}
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <LuPackage size={17} />
              </Flex>
              <Box flex="1" minW={0}>
                <Text fontSize="sm" fontWeight="600" color={themeColors.text}>{r.titulo}</Text>
                <Flex gap={3} flexWrap="wrap">
                  {target && (
                    <Text fontSize="11px" fontFamily="monospace" color={themeColors.textMuted}>{target}</Text>
                  )}
                  <Text fontSize="11px" color={themeColors.textMuted}>
                    {formatDate(r.fechaProduccion)}
                  </Text>
                </Flex>
              </Box>
              {target && (
                <Flex alignItems="center" gap={1} color={brand.green} flexShrink={0}>
                  <Text fontSize="xs" fontWeight="700">
                    {t('expedientes.electronico.relacionados.open')}
                  </Text>
                  <LuArrowUpRight size={14} />
                </Flex>
              )}
            </Flex>
          </MotionBox>
        )
      })}
    </VStack>
  )
}
